import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Camera, MapPin, Phone, Mail, User, Loader2, Calendar } from "lucide-react";
import { getProfile, UpdateProfile } from "../ApiHelper/url_Helpers";
import { toasterSuccess, toasterError } from "./toaster";
import Loader from "./Loader";

const ProfileForm = () => {
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const formik = useFormik({
    initialValues: {
      first_name: "",
      last_name: "",
      email: "",
      mobile_number: "",
      date_of_birth: "",
      profile_image: null,
    },
    validationSchema: Yup.object({
      first_name: Yup.string()
        .min(3, "First name must be at least 3 characters")
        .required("First name is required"),
      last_name: Yup.string()
        .optional(),
      email: Yup.string()
        .email("Invalid email address")
        .required("Email is required"),
      mobile_number: Yup.string()
        .matches(/^[0-9]{10}$/, 'Must be a valid 10-digit mobile number')
        .required("Mobile number is required"),
      date_of_birth: Yup.date().required("Date of Birth is required")
        .max(
          new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
          "Must be at least 18 years old",
        ),
      profile_image: Yup.mixed()
        .test("fileType", "Only JPG and PNG are supported", (value) => {
          if (!value || typeof value === "string") return true;
          return ["image/jpeg", "image/png", "image/jpg"].includes(value.type);
        })
        .optional(),
    }),
    onSubmit: async (values) => {
      setUpdating(true);
      try {
        const formData = new FormData();
        Object.keys(values).forEach((key) => {
          if (key === 'profile_image') {
            if (values[key] instanceof File) {
              formData.append(key, values[key]);
            }
          } else if (values[key] !== null && values[key] !== undefined) {
            formData.append(key, values[key]);
          }
        });

       const resp =  await UpdateProfile(formData);
        await fetchProfile();
        toasterSuccess("Profile updated successfully!");
        window.dispatchEvent(new Event("profileUpdate"));
      } catch (error) {
        toasterError(error?.message || "Failed to update profile");
      } finally {
       setTimeout(()=>{
        setUpdating(false);
      }, 1000)
      }
    },
  });

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const response = await getProfile();
      const basic_info = response?.basic_info?.data || {};
      const personal_info = response?.personal_info?.data || {};

      formik.resetForm({
        values: {
          first_name: basic_info.first_name || "",
          last_name: basic_info.last_name || "",
          email: basic_info.email || "",
          mobile_number: basic_info.mobile_number || "",
          date_of_birth: personal_info.date_of_birth || "",
          profile_image: basic_info.profile_image || null,
        }
      });
      if (basic_info?.profile_image) {
        setPreviewImage(basic_info.profile_image);
        localStorage.setItem("profilepic", basic_info?.profile_image);
      }
      if (basic_info?.first_name) {
        localStorage.setItem("username", `${basic_info.first_name} ${basic_info.last_name || ""}`);
      }
    } catch (error) {
      toasterError("Failed to fetch profile details");
    } finally {
      setTimeout(()=>{
        setLoading(false);
      }, 1000)
    }
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const handleImageChange = (e) => {
    const file = e.currentTarget.files[0];
    if (file) {
      formik.setFieldValue("profile_image", file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="flex flex-col items-center space-y-4">
          <div className="w-32 h-32 rounded-full bg-gray-200" />
          <div className="w-24 h-4 bg-gray-200 rounded" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="space-y-2">
              <div className="w-20 h-4 bg-gray-200 rounded" />
              <div className="w-full h-10 bg-gray-100 rounded-lg" />
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <div className="w-32 h-10 bg-gray-200 rounded-lg" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <form onSubmit={formik.handleSubmit} className="space-y-6">
        {/* Profile Image SECTION */}
        <div className="flex flex-col items-center space-y-4">
          <div className="relative group">
            <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-100 flex items-center justify-center">
              {previewImage ? (
                <img src={previewImage} alt="Profile" className="w-full h-full object-cover" />
              ) : (
                <User className="w-16 h-16 text-gray-400" />
              )}
            </div>
            <label className="absolute bottom-1 right-1 bg-[#00B8D9] text-white p-2 rounded-full cursor-pointer shadow-md hover:bg-[#009cb8] transition-colors group">
              <Camera size={18} />
              <input
                type="file"
                name="profile_image"
                className="hidden"
                accept=".jpg, .jpeg, .png"
                onChange={handleImageChange}
              />
            </label>
          </div>
          {formik.errors.profile_image && (
            <p className="text-red-500 text-xs mt-1">{formik.errors.profile_image}</p>
          )}
          <p className="text-sm text-gray-500">JPG, PNG allowed.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First Name */}
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">First Name</label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-400">
                <User size={18} />
              </span>
              <input
                type="text"
                name="first_name"
                {...formik.getFieldProps("first_name")}
                className={`w-full pl-10 pr-4 py-2 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 transition-all ${formik.touched.first_name && formik.errors.first_name
                    ? "border-red-500 focus:ring-red-200"
                    : "border-gray-200 focus:ring-cyan-100 focus:border-[#00B8D9]"
                  }`}
                placeholder="John"
              />
            </div>
            {formik.touched.first_name && formik.errors.first_name && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.first_name}</p>
            )}
          </div>

          {/* Last Name */}
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">Last Name</label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-400">
                <User size={18} />
              </span>
              <input
                type="text"
                name="last_name"
                {...formik.getFieldProps("last_name")}
                className={`w-full pl-10 pr-4 py-2 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 transition-all ${formik.touched.last_name && formik.errors.last_name
                    ? "border-red-500 focus:ring-red-200"
                    : "border-gray-200 focus:ring-cyan-100 focus:border-[#00B8D9]"
                  }`}
                placeholder="Doe"
              />
            </div>
            {formik.touched.last_name && formik.errors.last_name && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.last_name}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">
              Email Address {formik.initialValues.email && "(Readonly)"}
            </label>
            <div className="relative">
              <span className={`absolute left-3 top-3 ${formik.initialValues.email ? "text-gray-400" : "text-gray-400"}`}>
                <Mail size={18} />
              </span>
              <input
                type="email"
                name="email"
                readOnly={!!formik.initialValues.email}
                {...formik.getFieldProps("email")}
                className={`w-full pl-10 pr-4 py-2 border rounded-lg transition-all focus:outline-none ${
                  formik.initialValues.email
                    ? "bg-gray-100 border-gray-200 text-gray-500 cursor-not-allowed"
                    : `bg-gray-50 ${
                        formik.touched.email && formik.errors.email
                          ? "border-red-500 focus:ring-2 focus:ring-red-200"
                          : "border-gray-200 focus:ring-2 focus:ring-cyan-100 focus:border-[#00B8D9]"
                      }`
                }`}
                placeholder="email@example.com"
              />
            </div>
            {formik.touched.email && formik.errors.email && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.email}</p>
            )}
          </div>

          {/* Mobile Number */}
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">Mobile Number</label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-400">
                <Phone size={18} />
              </span>
              <input
                type="text"
                name="mobile_number"
                {...formik.getFieldProps("mobile_number")}
                className={`w-full pl-10 pr-4 py-2 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 transition-all ${formik.touched.mobile_number && formik.errors.mobile_number
                    ? "border-red-500 focus:ring-red-200"
                    : "border-gray-200 focus:ring-cyan-100 focus:border-[#00B8D9]"
                  }`}
                placeholder="9876543210"
              />
            </div>
            {formik.touched.mobile_number && formik.errors.mobile_number && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.mobile_number}</p>
            )}
          </div>

          {/* Date of Birth */}
          <div className="space-y-1">
            <label className="text-sm font-semibold text-gray-700">Date of Birth</label>
            <div className="relative">
              <span className="absolute left-3 top-3 text-gray-400">
                <Calendar size={18} />
              </span>
              <input
                type="date"
                name="date_of_birth"
                {...formik.getFieldProps("date_of_birth")}
                className={`w-full pl-10 pr-4 py-2 bg-gray-50 border rounded-lg focus:outline-none focus:ring-2 transition-all ${formik.touched.date_of_birth && formik.errors.date_of_birth
                    ? "border-red-500 focus:ring-red-200"
                    : "border-gray-200 focus:ring-cyan-100 focus:border-[#00B8D9]"
                  }`}
              />
            </div>
            {formik.touched.date_of_birth && formik.errors.date_of_birth && (
              <p className="text-red-500 text-xs mt-1">{formik.errors.date_of_birth}</p>
            )}
          </div>
        </div>

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={updating || !formik.dirty}
            className={`flex items-center justify-center space-x-2 px-8 py-2.5 rounded-lg text-white font-semibold transition-all shadow-md ${updating || !formik.dirty ? "bg-gray-400 cursor-not-allowed" : "bg-[#00B8D9] hover:bg-[#009cb8] hover:shadow-lg active:scale-95"
              }`}
          >
            {updating && <Loader2 className="animate-spin w-5 h-5" />}
            <span>{updating ? "Saving Changes..." : "Save Changes"}</span>
          </button>
        </div>
      </form>
      {updating || loading &&
        <Loader />
      }
    </div>
  );
};

export default ProfileForm;

